import { datadogLogs } from '@datadog/browser-logs'
import axios from 'axios'
import { getCoreRespondentBaseUrl } from 'utils/coreRespondentUrl'
import { useQuestionnaireParams } from './useQuestionnaireParams'

export enum CheckResult {
  Passed = 'passed',
  Failed = 'failed',
  Skipped = 'skipped',
  // The remaining members record context from within the frontend itself, not an outcome of the quality check
  TimedOut = 'timed_out',
  Error = 'error'
}

export type QualityCheckMetadata = Record<string, unknown> | null

export interface RespondentDataQualityCheckResponse {
  survey_id?: string
  supplier_respondent_id?: string
  check_result?: CheckResult
  metadata?: QualityCheckMetadata
}

export interface QualityCheckResult {
  surveyId?: string
  supplierRespondentId?: string
  checkResult?: CheckResult
  metadata?: QualityCheckMetadata
}

const usePerformQualityCheck = () => {
  const mandatoryQueryParams = useQuestionnaireParams()

  const performQualityCheck = async (): Promise<QualityCheckResult> => {
    if (!mandatoryQueryParams) {
      datadogLogs.logger.error(
        'Quality check skipped: Missing mandatory query params'
      )
      return {
        checkResult: CheckResult.Skipped
      }
    }

    const { surveyId, respondentId } = mandatoryQueryParams

    // Get the core respondent URL from environment
    const coreRespondentBaseUrl = getCoreRespondentBaseUrl()

    if (!coreRespondentBaseUrl) {
      datadogLogs.logger.warn(
        'Quality check skipped: FD_CORE_RESPONDENT_URL not configured'
      )
      return {
        surveyId,
        supplierRespondentId: respondentId,
        checkResult: CheckResult.Skipped
      }
    }

    // Get fraction of respondents that should trigger quality check (0-1), defaults to 0
    const qualityCheckFraction = parseFloat(
      process.env.REACT_APP_QUALITY_CHECK_FRACTION || '0'
    )

    // Generate a random number between 0-1 and check if it falls within the fraction
    const randomValue = Math.random()

    if (randomValue >= qualityCheckFraction) {
      datadogLogs.logger.info(
        'Quality check skipped due to percentage sampling',
        {
          qualityCheckFraction,
          randomValue,
          surveyId,
          respondentId
        }
      )
      return {
        surveyId,
        supplierRespondentId: respondentId,
        checkResult: CheckResult.Skipped
      }
    }

    // Get timeout from environment variable -- defaults to 5000ms if not configured
    const timeoutMs = parseInt(
      process.env.REACT_APP_QUALITY_CHECK_TIMEOUT_MS || '5000',
      10
    )

    // Filter by rules questions to ensure we only responses to questions for which rules exist
    const endpoint = `${coreRespondentBaseUrl}/surveys/${surveyId}/respondent/${respondentId}/data_quality/check?filter_by_rules_questions=true&include_metadata=true`

    datadogLogs.logger.info('Performing quality check', {
      surveyId,
      respondentId,
      timeoutMs
    })

    const requestStartTime = performance.now()

    try {
      const qualityCheckResponse = await axios.post(
        endpoint,
        {},
        {
          timeout: timeoutMs
        }
      )

      const requestDuration = performance.now() - requestStartTime

      const qualityCheckData = qualityCheckResponse?.data as
        | RespondentDataQualityCheckResponse
        | undefined

      if (!qualityCheckData) {
        datadogLogs.logger.error('Quality check response missing data: ', {
          surveyId,
          respondentId,
          requestDurationMs: Math.round(requestDuration)
        })
        return {
          surveyId,
          supplierRespondentId: respondentId,
          checkResult: CheckResult.Error
        }
      }

      datadogLogs.logger.info('Quality check completed', {
        surveyId,
        respondentId,
        checkResult: qualityCheckData.check_result,
        requestDurationMs: Math.round(requestDuration)
      })

      return {
        surveyId,
        supplierRespondentId: respondentId,
        checkResult: qualityCheckData.check_result,
        metadata: qualityCheckData.metadata
      }
    } catch (error) {
      const requestDuration = performance.now() - requestStartTime
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        datadogLogs.logger.warn('Quality check timed out: ', {
          timeoutMs,
          surveyId,
          respondentId,
          requestDurationMs: Math.round(requestDuration)
        })
        return {
          surveyId,
          supplierRespondentId: respondentId,
          checkResult: CheckResult.TimedOut
        }
      }
      datadogLogs.logger.error('Quality check failed', {
        error,
        surveyId,
        respondentId,
        requestDurationMs: Math.round(requestDuration)
      })
      return {
        surveyId,
        supplierRespondentId: respondentId,
        checkResult: CheckResult.Error
      }
    }
  }

  return performQualityCheck
}

export default usePerformQualityCheck
